import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { NewVisitorFeed, VisitorFeed } from 'types';
import { RejectError } from 'shared/api/axios';
import {
  deleteVisitorFeed,
  getVisitorFeeds,
  patchVisitorFeed,
  postVisitorFeed,
  putVisitorFeedOrder,
} from 'shared/api';
import type { AppState } from './reducers';
import { selectVisitorList } from './shared-selectors';

// STATE
export const initialState = {
  isFetchingFeeds: false,
  feeds: null as VisitorFeed[] | null,
};

export type FeedState = typeof initialState;

// THUNKS
export const fetchVisitorFeeds = createAsyncThunk<
  VisitorFeed[],
  void,
  { state: AppState }
>('feed/fetchVisitorFeeds', async () => {
  return (await getVisitorFeeds()).data;
});

export const addVisitorFeed = createAsyncThunk<
  VisitorFeed,
  NewVisitorFeed,
  { state: AppState; rejectValue: RejectError }
>('feed/addVisitorFeed', async data => {
  return (await postVisitorFeed(data)).data;
});

export const editVisitorFeed = createAsyncThunk<
  VisitorFeed,
  { id: number; feed: NewVisitorFeed },
  { state: AppState; rejectValue: RejectError }
>('feed/addVisitorFeed', async ({ id, feed }) => {
  return (await patchVisitorFeed(id, feed)).data;
});

export const removeVisitorFeed = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('feed/removeVisitorFeed', async id => {
  await deleteVisitorFeed(id);
});

export const reorderVisitorFeed = createAsyncThunk<
  VisitorFeed[],
  number[],
  { state: AppState; rejectValue: RejectError }
>('feed/reorderVisitorFeed', async orderedIds => {
  return (await putVisitorFeedOrder(orderedIds)).data;
});

// SLICE
const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // fetchFeed
    builder
      .addCase(fetchVisitorFeeds.pending, state => {
        state.isFetchingFeeds = true;
      })
      .addCase(fetchVisitorFeeds.fulfilled, (state, action) => {
        state.isFetchingFeeds = false;
        state.feeds = action.payload;
      })
      .addCase(fetchVisitorFeeds.rejected, state => {
        state.isFetchingFeeds = false;
      });
  },
});

// SELECTORS
export const selectFeedVisitorList = createSelector(
  selectVisitorList,
  (_: AppState, feedId: number | string) => feedId,
  (visitorList, feedId) => {
    if (typeof feedId !== 'number') return visitorList;
    return visitorList.filter(({ streams }) => streams.includes(feedId));
  },
);

export default feedSlice.reducer;
